import React, { forwardRef, ReactNode } from 'react';
import { cx } from '@linaria/core';
import { styled } from '@linaria/react';
import { SizesType } from 'types/Sizes';
import { CSSDescriptionType } from 'types/Theme';
import { cssTheme } from '../../../themes';
import { getLinariaClassName } from '../../../utils/getLinariaClassName';
import { LoadingBullets } from './LoadingBullets';
const BUTTON_VARIANTS = ['primary', 'secondary', 'special', 'quiet', 'transparent', 'inverse', 'danger'] as const;
type ButtonVariantTuple = typeof BUTTON_VARIANTS;
export type Variant = ButtonVariantTuple[number];
export type ButtonSize = Exclude<SizesType, 'xs' | 'xxl'>;
export interface Props {
  /** Identify the element for selection in integration tests, FullStory, etc. */
  dataId?: string;
  /** Button content */
  children?: React.ReactNode;
  /** Pass through classname to allow styles overrides */
  className?: string;
  /** Button style */
  variant?: Variant;
  /** Whether the button is in the loading state */
  loading?: boolean;
  /** The button click handler */
  onClick?: (e: React.SyntheticEvent) => void;
  /** The button size */
  size?: ButtonSize;
  /** The HTML button type */
  type?: 'submit' | 'reset' | 'button' | undefined;
  /** Whether the button full width of parent */
  fullWidth?: boolean;
  /** Whether the button is  disabled */
  disabled?: boolean;
  /** Whether the button is  rounded */
  isRounded?: boolean;
  /** An Icon to display on the left side of the input for LTR languages. Inverted on RTL */
  iconStart?: ReactNode;
  /** An Icon to display on the right side of the input for LTR languages. Inverted on RTL */
  iconEnd?: ReactNode;
  /** To add drop shadow styling */
  isFloating?: boolean;
  /** Use larger touch area */
  hasTouchArea?: boolean;
  form?: string;
  /** Optional aria-label for screen-readers, used for icon-only buttons */
  ariaLabel?: string;
  /** Optional style object */
  style?: React.CSSProperties;
}
interface ButtonSizeMap {
  size: number;
  sizeWithTouchArea: number;
  basePadding: number;
  horizontalPadding?: number;
  typography: CSSDescriptionType;
}
export const buttonStyleVariants = [...BUTTON_VARIANTS, 'disabled'];
export const buttonSizesMap: Record<ButtonSize, ButtonSizeMap> = {
  sm: {
    size: 26,
    sizeWithTouchArea: 44,
    basePadding: 4,
    typography: cssTheme.typography.labelXS
  },
  md: {
    size: 34,
    sizeWithTouchArea: 44,
    basePadding: 6,
    typography: cssTheme.typography.labelS
  },
  lg: {
    size: 42,
    sizeWithTouchArea: 44,
    basePadding: 8,
    typography: cssTheme.typography.labelM
  },
  xl: {
    size: 48,
    sizeWithTouchArea: 48,
    basePadding: 8,
    horizontalPadding: 16,
    typography: cssTheme.typography.labelM
  }
};
export const StyledButtonContent = "so1zfzq";
const StyledButton = /*#__PURE__*/styled('button')({
  name: "StyledButton",
  class: "sben74z",
  propsAsIs: false
});
export const IconWrapper = /*#__PURE__*/styled('div')({
  name: "IconWrapper",
  class: "i157tbn9",
  propsAsIs: false
});
const _exp8 = /*#__PURE__*/() => ({
  size
}) => `0 ${buttonSizesMap[size].basePadding}px`;
export const LabelWrapper = /*#__PURE__*/styled('div')({
  name: "LabelWrapper",
  class: "l1yv57s",
  propsAsIs: false,
  vars: {
    "l1yv57s-0": [_exp8()]
  }
});
export const Button = forwardRef(({
  variant = 'primary',
  size = 'lg',
  loading = false,
  disabled = false,
  fullWidth = false,
  isRounded = false,
  className = '',
  dataId = '',
  type = 'button',
  onClick,
  children,
  iconStart,
  iconEnd,
  isFloating = false,
  hasTouchArea = false,
  form,
  ariaLabel,
  style
}: Props, ref: React.ForwardedRef<HTMLButtonElement>) => {
  const disabledState = loading || disabled;
  return <StyledButton ref={ref} data-id={dataId} className={cx(`${getLinariaClassName(StyledButton)}--size-${size}`, `${getLinariaClassName(StyledButton)}--variant-${disabled ? 'disabled' : variant}`, fullWidth && 'fullWidth', hasTouchArea && 'hasTouchArea', className)} type={type} disabled={disabledState} onClick={onClick} form={form} aria-label={ariaLabel} style={style}>
        <div className={cx(StyledButtonContent, `${StyledButtonContent}--size-${size}`, isRounded && 'rounded', isFloating && !disabled && 'hasFloatingShadow')}>
          {loading ? <LoadingBullets size={size} /> : <>
              {iconStart && <IconWrapper>{iconStart}</IconWrapper>}

              {children && <LabelWrapper size={size}>{children}</LabelWrapper>}

              {iconEnd && <IconWrapper>{iconEnd}</IconWrapper>}
            </>}
        </div>
      </StyledButton>;
});
Button.displayName = 'Button';

require("./Button.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./Button.tsx");